import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

const PolicySection = styled.section`
  padding: 6rem 0 4rem;
  background-color: ${theme.colors.lightGray};
`;

const PolicyContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background-color: ${theme.colors.white};
  padding: 3rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 2rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const UpdateDate = styled.p`
  color: ${theme.colors.darkGray};
  font-size: ${theme.fontSizes.small};
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid ${theme.colors.lightGray};
`;

const ContentSection = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const SectionContent = styled.div`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.8;
  color: ${theme.colors.secondary};
  
  p {
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.75rem;
  }
  
  strong {
    font-weight: 700;
    color: ${theme.colors.secondary};
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${theme.colors.accent};
    }
  }
  
  h3 {
    font-size: ${theme.fontSizes.large};
    margin: 1.5rem 0 1rem;
    color: ${theme.colors.secondary};
  }
`;

const HighlightBox = styled.div`
  background-color: rgba(139, 92, 246, 0.1);
  border-left: 4px solid ${theme.colors.primary};
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-radius: 8px;
  
  strong {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const GDPRPage = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        titleKey="gdpr.seo.title"
        descriptionKey="gdpr.seo.description"
      />
      
      <PolicySection>
        <div className="container">
          <PolicyContainer>
            <PageTitle>{t('gdpr.title')}</PageTitle>
            <UpdateDate>{t('gdpr.updateDate')}</UpdateDate>
            
            <ContentSection>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.intro') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.dataController.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.dataController.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.personalData.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.personalData.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.processingPurposes.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.processingPurposes.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.legalBasis.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.legalBasis.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.dataSharing.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.dataSharing.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.dataRetention.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.dataRetention.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.yourRights.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.yourRights.content') }} />
              
              <HighlightBox>
                <strong>{t('gdpr.yourRights.highlight.title')}</strong>
                <p dangerouslySetInnerHTML={{ __html: t('gdpr.yourRights.highlight.content') }} />
              </HighlightBox>
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.security.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.security.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.contact.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.contact.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('gdpr.changes.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('gdpr.changes.content') }} />
            </ContentSection>
          </PolicyContainer>
        </div>
      </PolicySection>
    </>
  );
};

export default GDPRPage;

